import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Alert,
  useTheme,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { getBookingList, getStayingBookings, getBookingDetails } from '../services/api';
import BookingDetailsDrawer from '../components/booking/BookingDetailsDrawer';
import BookingOpsBookingCard from '../components/booking/BookingOpsBookingCard';
import DashboardHeader from '../components/common/DashboardHeader';
import EmptyStateCard from '../components/common/EmptyStateCard';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getDashboardPageShellSx, getDashboardPaperSx, resolveDashboardThemeMode } from '../utils/dashboardSurfaceSx';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import { patchBookingInList } from '../utils/bookingListPatch';
import {
  getBookingOpsControlsLayoutSx,
  getBookingOpsDateFieldSx,
  getBookingOpsDatePickerBoxSx,
  getBookingOpsDateRowSx,
  getBookingOpsFilterToggleSx,
  getBookingOpsFiltersRowSx,
  getBookingOpsGridItemSx,
  getBookingOpsNavButtonSx,
  getBookingOpsTotalLabelSx,
  getBookingOpsTotalRowSx,
  getBookingOpsTotalValueSx,
  getStayFilterLabel,
} from '../utils/bookingOperationsMobileLayout';
import { buildBookingRoomChips } from '../utils/bookingRoomChips';
import { normalizeBookingStatusValue } from '../utils/bookingOpsCardHelpers';
import { getHotelDateFormat } from '../utils/dateUtils';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

const StayPage = ({ mode = 'stay' }) => {
  const normalizeBookingId = (value) => {
    const parsedId = Number(value);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  };
  const isBookingMode = mode === 'booking_date';
  const theme = useTheme();
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isMobileLayout = isMobileShellLayout();
  const isDark = resolveDashboardThemeMode(currentTheme) === 'dark';

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const [selectedDate, setSelectedDate] = useState(moment());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // 3 rows × 4 cards per row
  const fetchAbortController = useRef(null);
  const debounceTimer = useRef(null);

  // Check if checkout date has passed the hotel's checkout time
  const isDelayedCheckout = useCallback((checkoutDate, hotelCheckoutTime) => {
    if (!checkoutDate) return false;
    
    const checkoutMoment = moment(checkoutDate);
    const today = moment();
    
    // Parse hotel checkout time (format: "HH:mm:ss" or "HH:mm")
    let checkoutTimeStr = hotelCheckoutTime || '11:00:00';
    if (checkoutTimeStr.length === 5) {
      checkoutTimeStr = checkoutTimeStr + ':00'; // Add seconds if missing
    }
    
    // Create checkout datetime with hotel's checkout time
    const checkoutDateTime = checkoutMoment.clone().set({
      hour: parseInt(checkoutTimeStr.split(':')[0]),
      minute: parseInt(checkoutTimeStr.split(':')[1]),
      second: 0,
      millisecond: 0,
    });
    
    // If checkout date is today and current time is past hotel checkout time, or checkout date is in the past
    if (checkoutMoment.isSame(today, 'day')) {
      return today.isAfter(checkoutDateTime);
    }
    return checkoutMoment.isBefore(today, 'day');
  }, []);

  // Debounced fetch to prevent multiple rapid calls
  useEffect(() => {
    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Cancel any ongoing fetch
    if (fetchAbortController.current) {
      fetchAbortController.current.abort();
    }

    // Debounce the fetch
    debounceTimer.current = setTimeout(() => {
      setHasFetched(false); // Reset fetch status when date changes
      fetchBookings();
      setCurrentPage(1); // Reset to first page when date changes
    }, 300); // 300ms debounce

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (fetchAbortController.current) {
        fetchAbortController.current.abort();
      }
    };
  }, [isBookingMode, selectedDate]);

  const fetchBookings = useCallback(async () => {
    // Create new AbortController for this fetch
    fetchAbortController.current = new AbortController();
    const signal = fetchAbortController.current.signal;

    setLoading(true);
    setError(null);
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      let nextBookings = [];
      if (isBookingMode) {
        const data = await getBookingList({
          booking_date: dateStr,
          per_page: 500,
          sort: '-created_at',
        });
        nextBookings = data?.Data || [];
      } else {
        const data = await getStayingBookings(dateStr, {
          includePendingCheckout: true,
          pendingLookbackDays: 14,
        });
        nextBookings = data?.bookings || [];
      }
      if (signal.aborted) return;
      setBookings(nextBookings);
      setHasFetched(true);
    } catch (err) {
      if (!err.name || err.name !== 'AbortError') {
        setError(
          isBookingMode
            ? 'Failed to load bookings. Please try again.'
            : 'Failed to load staying bookings. Please try again.'
        );
        setHasFetched(true);
      }
    } finally {
      if (!fetchAbortController.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [isBookingMode, selectedDate]);

  /** Update one card after drawer payment — no full-list loading flash. */
  const refreshBookingCard = useCallback(async (bookingIdArg) => {
    const id = normalizeBookingId(bookingIdArg ?? selectedBookingId);
    if (!id) return;
    try {
      const details = await getBookingDetails(id);
      setBookings((prev) => patchBookingInList(prev, id, details, normalizeBookingId));
    } catch {
      // Keep existing cards; avoid forcing a full-list reload.
    }
  }, [selectedBookingId]);

  const handleDateChange = (newDate) => {
    if (newDate && moment.isMoment(newDate)) {
      setSelectedDate(newDate);
    }
  };

  const handlePrevClick = () => {
    const newDate = selectedDate.clone().subtract(1, 'days');
    setSelectedDate(newDate);
  };

  const handleNextClick = () => {
    const newDate = selectedDate.clone().add(1, 'days');
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(moment());
  };

  const handleBookingClick = (bookingId) => {
    const safeBookingId = normalizeBookingId(bookingId);
    if (!safeBookingId) {
      setError('Invalid booking reference. Please refresh and try again.');
      return;
    }
    setSelectedBookingId(safeBookingId);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedBookingId(null);
  };

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilterType(newFilter);
      setCurrentPage(1);
    }
  };

  // Filter bookings based on selected filter - memoized for performance
  const filteredBookings = useMemo(() => {
    const getNormalizedStatus = (booking) => normalizeBookingStatusValue(booking);

    if (isBookingMode) {
      if (filterType === 'booked') {
        return bookings.filter((booking) => {
          const status = getNormalizedStatus(booking);
          return status !== 'cancelled' && status !== 'canceled';
        });
      }
      if (filterType === 'cancelled') {
        return bookings.filter((booking) => {
          const status = getNormalizedStatus(booking);
          return status === 'cancelled' || status === 'canceled';
        });
      }
      return bookings;
    }

    const isInHouseStatus = (status) =>
      status === 'checked_in' || status === 'partial_checkin' || status === 'partial_checkout';

    const isPendingCheckoutBooking = (booking) => {
      const statusValue = getNormalizedStatus(booking);
      if (booking?.is_pending_checkout !== undefined && booking?.is_pending_checkout !== null) {
        return !!booking.is_pending_checkout;
      }
      return (
        isInHouseStatus(statusValue) &&
        isDelayedCheckout(booking?.check_out_date, booking?.hotel_checkout_time)
      );
    };

    if (filterType === 'all') {
      return bookings;
    } else if (filterType === 'pending_checkout') {
      return bookings.filter((booking) => isPendingCheckoutBooking(booking));
    } else if (filterType === 'continuing') {
      return bookings.filter(
        (booking) => isInHouseStatus(getNormalizedStatus(booking)) && !isPendingCheckoutBooking(booking)
      );
    }
    return bookings;
  }, [bookings, filterType, isBookingMode, isDelayedCheckout]);

  // Pagination logic
  const paginatedBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredBookings.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredBookings, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getRoomsCount = (booking) => {
    const explicitRooms = Number(booking?.rooms ?? booking?.total_rooms);
    if (Number.isFinite(explicitRooms) && explicitRooms > 0) {
      return explicitRooms;
    }

    const bookingItems = Array.isArray(booking?.booking_items) ? booking.booking_items : [];
    if (!bookingItems.length) return 0;

    const sequences = new Set(
      bookingItems
        .map((item) => Number(item?.room_sequence))
        .filter((value) => Number.isInteger(value) && value > 0)
    );
    if (sequences.size > 0) return sequences.size;

    // Fallback for payloads without room_sequence:
    // infer room count as maximum concurrent items on the same date.
    const countByDate = bookingItems.reduce((acc, item) => {
      const key = item?.date ? String(item.date).slice(0, 10) : '';
      if (!key) return acc;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const maxByDate = Object.values(countByDate).reduce(
      (maxCount, current) => Math.max(maxCount, Number(current) || 0),
      0
    );
    return maxByDate || bookingItems.length;
  };

  const buildStayRoomChips = useCallback((booking) => {
    const baseChips = buildBookingRoomChips(booking);
    const bookingItems = Array.isArray(booking?.booking_items) ? booking.booking_items : [];
    if (bookingItems.length === 0) return baseChips;

    const getDateKey = (item) => {
      if (!item?.date) return '';
      return moment.isMoment(item.date) ? item.date.format('YYYY-MM-DD') : String(item.date).slice(0, 10);
    };
    const getRoomLabel = (item) =>
      item?.allocated_room_number ||
      item?.allocated_room?.room_number ||
      item?.allocated_room?.number ||
      (item?.allocated_room_id ? `Room ${item.allocated_room_id}` : null);

    const sortedItems = [...bookingItems].sort((a, b) => {
      const dateA = getDateKey(a);
      const dateB = getDateKey(b);
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      const seqA = Number(a?.room_sequence || 1);
      const seqB = Number(b?.room_sequence || 1);
      if (seqA !== seqB) return seqA - seqB;
      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    });
    const getTypeId = (item) => Number((item?.room_type_id ?? item?.room_type?.id) || 0);
    const isCheckedOutItem = (item) => {
      const raw = item?.checkout;
      if (typeof raw === 'string') return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
      return raw === true || raw === 1;
    };
    const normalizeRoomForLink = (value) => String(value || '').replace(/^Room\s+/i, '').trim();

    const upgradeLinkMap = new Map();
    sortedItems.forEach((item) => {
      const upgradeFromTypeId = Number(
        (item?.upgraded_from_room_type_id ?? item?.upgraded_from_room_type?.id) || 0
      );
      if (!upgradeFromTypeId) return;
      const itemDate = getDateKey(item);
      const toRoom = getRoomLabel(item);
      if (!itemDate || !toRoom) return;

      const seq = Number(item?.room_sequence || 1);
      const previousItem = [...sortedItems]
        .reverse()
        .find((prev) => {
          const prevDate = getDateKey(prev);
          const prevRoom = getRoomLabel(prev);
          if (!prevDate || !prevRoom || prevDate >= itemDate) return false;
          const prevTypeId = getTypeId(prev);
          if (prevTypeId && prevTypeId === upgradeFromTypeId) return true;
          return Number(prev?.room_sequence || 1) === seq;
        });
      const fromRoom = previousItem ? getRoomLabel(previousItem) : null;
      if (!fromRoom || fromRoom === toRoom) return;

      const linkLabel = `[${normalizeRoomForLink(fromRoom)}->${normalizeRoomForLink(toRoom)}]`;
      if (!upgradeLinkMap.has(linkLabel)) {
        upgradeLinkMap.set(linkLabel, {
          key: `upgrade_link_${seq}_${linkLabel}`,
          label: linkLabel,
        });
      }
    });

    // Fallback when backend linkage is missing:
    // derive a single room transition from last checked-out room -> first active room.
    if (upgradeLinkMap.size === 0) {
      const checkedOutCandidates = sortedItems.filter((it) => isCheckedOutItem(it) && getRoomLabel(it) && getDateKey(it));
      const activeCandidates = sortedItems.filter((it) => !isCheckedOutItem(it) && getRoomLabel(it) && getDateKey(it));
      const fromItem = [...checkedOutCandidates].sort((a, b) => getDateKey(b).localeCompare(getDateKey(a)))[0];
      const toItem = [...activeCandidates].sort((a, b) => getDateKey(a).localeCompare(getDateKey(b)))[0];
      const fromRoom = fromItem ? getRoomLabel(fromItem) : null;
      const toRoom = toItem ? getRoomLabel(toItem) : null;
      if (fromRoom && toRoom && fromRoom !== toRoom) {
        const fallbackLabel = `[${normalizeRoomForLink(fromRoom)}->${normalizeRoomForLink(toRoom)}]`;
        upgradeLinkMap.set(fallbackLabel, { key: `upgrade_link_fallback_${fallbackLabel}`, label: fallbackLabel });
      }
    }

    const upgradeChips = Array.from(upgradeLinkMap.values());
    if (!upgradeChips.length) return baseChips;

    const normalizeRoomText = (value) =>
      String(value || '')
        .replace(/^\[/, '')
        .replace(/\]$/, '')
        .replace(/^Room\s+/i, '')
        .trim();

    const linkEndpoints = new Set();
    const linkTexts = new Set();
    upgradeChips.forEach((chip) => {
      const txt = normalizeRoomText(chip?.label);
      if (!txt) return;
      linkTexts.add(txt);
      const parts = txt.split('->').map((p) => normalizeRoomText(p)).filter(Boolean);
      if (parts.length === 2) {
        linkEndpoints.add(parts[0]);
        linkEndpoints.add(parts[1]);
      }
    });

    const existingLabels = new Set(baseChips.map((chip) => chip?.label).filter(Boolean));
    const uniqueUpgradeChips = upgradeChips.filter((chip) => !existingLabels.has(chip.label));

    // When link chip exists, hide standalone endpoint chips (e.g. 104, 107)
    // so card shows concise upgrade mapping only.
    const filteredBaseChips = baseChips.filter((chip) => {
      const txt = normalizeRoomText(chip?.label);
      if (!txt) return false;
      if (linkTexts.has(txt)) return false;
      if (linkEndpoints.has(txt)) return false;
      return true;
    });

    return [...uniqueUpgradeChips, ...filteredBaseChips];
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={getDashboardPageShellSx(currentTheme)}>
        {/* Header */}
        <Box sx={{ mb: { xs: 1.5, sm: 3 } }}>
          <DashboardHeader
            title={isBookingMode ? 'Booking' : 'Stay'}
            forceDark={isMobileLayout}
            breadcrumbs={[
              { label: 'Home', path: '/dashboard/' },
              ...(isBookingMode
                ? [{ label: 'Booking' }]
                : [
                    { label: 'Booking', path: '/booking/' },
                    { label: 'Stay' },
                  ])
            ]}
            navLinks={[
              { url: '/booking/list/', label: 'Booking List' },
              ...(isBookingMode
                ? [
                    { url: '/booking/reservation/', label: 'Reservation' },
                    { url: '/booking/stay/', label: 'Stay' },
                  ]
                : [{ url: '/booking/checkin/', label: 'Check-In' }]),
            ]}
          />
        </Box>

        {/* Date Navigation and Total Staying - Same style as check-in */}
        <Paper
          elevation={0}
          sx={getDashboardPaperSx(currentTheme, 'controls')}
        >
          <Box sx={getBookingOpsControlsLayoutSx(isMobileLayout)}>
            {/* Datepicker on the left */}
            <Box
              sx={getBookingOpsDateRowSx(isMobileLayout)}
              className="dashboard-date-range"
            >
              <IconButton
                onClick={handlePrevClick}
                className="calendar-nav-button"
                sx={getBookingOpsNavButtonSx(isDark, CUSTOM_BORDER_COLOR)}
              >
                <ChevronLeft />
              </IconButton>
              <Box sx={getBookingOpsDatePickerBoxSx(isMobileLayout)} className="calendar-date-picker">
                <DatePicker
                  value={selectedDate}
                  onChange={handleDateChange}
                  format={getHotelDateFormat()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: getBookingOpsDateFieldSx(isDark, CUSTOM_BORDER_COLOR),
                    },
                  }}
                />
              </Box>
              <IconButton
                onClick={handleNextClick}
                className="calendar-nav-button"
                sx={getBookingOpsNavButtonSx(isDark, CUSTOM_BORDER_COLOR)}
              >
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Filter Toggles and Total on the right */}
            <Box sx={getBookingOpsFiltersRowSx(isMobileLayout)}>
              {/* Filter Toggle Buttons */}
              <ToggleButtonGroup
                value={filterType}
                exclusive
                onChange={handleFilterChange}
                aria-label="filter type"
                size="small"
                sx={getBookingOpsFilterToggleSx(isMobileLayout, isDark, CUSTOM_BORDER_COLOR)}
              >
                <ToggleButton value="all" aria-label="all">
                  {getStayFilterLabel('all', isMobileLayout)}
                </ToggleButton>
                {isBookingMode ? (
                  [
                    <ToggleButton key="booked" value="booked" aria-label="booked">
                      {getStayFilterLabel('booked', isMobileLayout)}
                    </ToggleButton>,
                    <ToggleButton key="cancelled" value="cancelled" aria-label="cancelled">
                      {getStayFilterLabel('cancelled', isMobileLayout)}
                    </ToggleButton>,
                  ]
                ) : (
                  [
                    <ToggleButton key="pending_checkout" value="pending_checkout" aria-label="pending_checkout">
                      {getStayFilterLabel('pending_checkout', isMobileLayout)}
                    </ToggleButton>,
                    <ToggleButton key="continuing" value="continuing" aria-label="continuing">
                      {getStayFilterLabel('continuing', isMobileLayout)}
                    </ToggleButton>,
                  ]
                )}
              </ToggleButtonGroup>

              {/* Total */}
              <Box sx={getBookingOpsTotalRowSx(isMobileLayout, isDark)}>
                <Typography variant="body2" sx={getBookingOpsTotalLabelSx(isDark)}>
                  Total:
                </Typography>
                <Typography variant="h6" sx={getBookingOpsTotalValueSx(isDark)}>
                  {filteredBookings.length}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Bookings Grid */}
      {!loading && !error && hasFetched && (
        <>
          {filteredBookings.length === 0 ? (
            <EmptyStateCard themeMode={currentTheme}>
              {isBookingMode
                ? `No bookings found for booking date ${selectedDate.format('DD MMM YYYY')}`
                : `No staying bookings found for ${selectedDate.format('DD MMM YYYY')}`}
            </EmptyStateCard>
          ) : (
            <>
              <Grid container spacing={{ xs: 1.5, sm: 3 }}>
                {paginatedBookings.map((booking) => {
                  const resolvedBookingId = normalizeBookingId(booking?.id || booking?.booking_id);
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      sx={getBookingOpsGridItemSx()}
                      key={resolvedBookingId || `${booking?.code || 'booking'}_${booking?.check_in_date || ''}_${booking?.check_out_date || ''}`}
                    >
                      <BookingOpsBookingCard
                        booking={booking}
                        isDark={isDark}
                        accent={CUSTOM_BORDER_COLOR}
                        isMobile={isMobileLayout}
                        onClick={() => handleBookingClick(resolvedBookingId)}
                        getRoomChips={buildStayRoomChips}
                        getRoomsCount={getRoomsCount}
                      />
                    </Grid>
                  );
                })}
              </Grid>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2.5, sm: 4 }, mb: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobileLayout ? 'small' : 'large'}
                    showFirstButton={!isMobileLayout}
                    showLastButton={!isMobileLayout}
                    siblingCount={isMobileLayout ? 0 : 1}
                    boundaryCount={isMobileLayout ? 1 : 1}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: isDark ? '#ddd' : '#333',
                        '&.Mui-selected': {
                          backgroundColor: CUSTOM_BORDER_COLOR,
                          color: '#000',
                          '&:hover': {
                            backgroundColor: CUSTOM_BORDER_COLOR,
                          },
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Booking Details Drawer */}
      {selectedBookingId && (
        <BookingDetailsDrawer
          bookingId={selectedBookingId}
          open={drawerOpen}
          onClose={handleDrawerClose}
          onStatusUpdate={refreshBookingCard}
        />
      )}
    </Box>
    </LocalizationProvider>
  );
};

export default StayPage;

